// src/pages/HeathrowMinibusHire.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import CcgPremiumReviews from "../components/CcgPremiumReviews";
import CcgRouteLhrStatusCompact from "../components/CcgRouteLhrStatusCompact";
import FloatingSupportChat from "../components/FloatingSupportChat";
import PostcodeQuotenewMini from "./PostcodeQuotenewMini";
import LiveStrip from "../components/LiveStrip";
import "./Home.css";
import "./CcgRoutePremium.css";
import "../styles/heathrowMinibus.css";

const seoTitle = "Heathrow Minibus Hire – 9 & 14 Seater Airport Transfers | CheapCabGrab";
const seoDescription = "Book 9 and 14 seater Heathrow minibuses for groups, families and corporate travel. Fixed-price airport transfers with licensed drivers, meet & greet options and luggage space.";
const seoCanonical = "https://www.cheapcabgrab.com/heathrow-minibus-hire";
const seoKeywords = "Heathrow minibus hire, 9 seater Heathrow, 14 seater Heathrow, group airport transfers, minibus Heathrow to London, CheapCabGrab minibus";
const seoOgTitle = "Heathrow Minibus Hire – 9 & 14 Seater Airport Transfers";
const seoOgDescription = "Group airport transfers made easy – book 9 or 14 seater minibuses from Heathrow to London, Slough, Windsor, Maidenhead and beyond with CheapCabGrab.";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "TaxiService",
  name: "CheapCabGrab – Heathrow Minibus Hire",
  url: seoCanonical,
  areaServed: [
    "Heathrow Airport",
    "Slough",
    "Windsor",
    "Maidenhead",
    "Central London",
  ],
  serviceType: "Minibus hire for airport transfers",
  provider: {
    "@type": "Organization",
    name: "CheapCabGrab",
  },
};

const HeathrowMinibusHire = () => {
  const scrollToForm = () => {
    const el = document.getElementById("heathrow-minibus-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
    };

    const applySeo = () => {
      document.title = seoTitle;

      let canonicalEl = document.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", seoCanonical);

      setMeta('meta[name="description"]', { name: "description", content: seoDescription });
      setMeta('meta[name="keywords"]', { name: "keywords", content: seoKeywords });
      setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      setMeta('meta[property="og:title"]', { property: "og:title", content: seoOgTitle });
      setMeta('meta[property="og:description"]', { property: "og:description", content: seoOgDescription });
      setMeta('meta[property="og:url"]', { property: "og:url", content: seoCanonical });

      let script = document.querySelector('script[data-ccg-jsonld="heathrow-minibus-hire"]');
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-ccg-jsonld", "heathrow-minibus-hire");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    };

    applySeo();
    const timers = [window.setTimeout(applySeo, 0), window.setTimeout(applySeo, 750)];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  return (
    <div className="heathrow-minibus-page route-page ccg-route-premium">
      <Helmet>
        <title>
          {seoTitle}
        </title>
        <meta
          name="description"
          content={seoDescription}
        />
        <link
          rel="canonical"
          href={seoCanonical}
        />
        <meta
          name="keywords"
          content={seoKeywords}
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={seoOgTitle}
        />
        <meta
          property="og:description"
          content={seoOgDescription}
        />
        <meta
          property="og:url"
          content={seoCanonical}
        />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <main className="heathrow-minibus-main route-main">
        <section className="ccg-route-hero ccg-heathrow-minibus-hero">
          <div className="ccg-route-hero-shell">
            <div className="ccg-route-hero-top">
              <div className="ccg-route-hero-copy">
                <span className="ccg-route-eyebrow">Heathrow group airport transfers</span>
                <h1>Heathrow Minibus Hire – 8 &amp; 16 Seater Transfers</h1>
                <p>Airport specialists for family trips, teams and groups</p>
                <p>
                  Book reliable minibuses from Heathrow Terminals 2–5 with licensed drivers,
                  flight tracking and meet &amp; greet. Fixed upfront fares and secure online
                  booking — no surge pricing.
                </p>
              </div>

              <div className="ccg-route-trust-cards" aria-label="CheapCabGrab Heathrow minibus trust signals">
                <article className="ccg-route-trust-card">
                  <b>✓</b>
                  <span>
                    <small>Heathrow coverage</small>
                    <strong>Terminals 2-5</strong>
                    <span>Pickup support for groups</span>
                  </span>
                </article>
                <article className="ccg-route-trust-card">
                  <b>G</b>
                  <span>
                    <small>Google reviews</small>
                    <strong>4.8 ★★★★★</strong>
                    <span>Trusted airport transfers</span>
                  </span>
                </article>
              </div>
            </div>

            <div className="ccg-route-quote-wrap" id="heathrow-minibus-quote-form">
              <div className="ccg-route-quote-head">
                <span className="ccg-route-quote-label">Get instant Heathrow minibus quotes</span>
                <span className="ccg-route-quote-note"><i /> Free - Fast - No obligation</span>
              </div>
              <PostcodeQuotenewMini presetPickup="Heathrow Airport" />
              <ul className="ccg-route-quote-promises">
                <li>Fixed upfront fare</li>
                <li>Licensed drivers</li>
                <li>Flight tracking</li>
                <li>Secure online booking</li>
              </ul>
            </div>

            <ul className="ccg-route-flags">
              <li>8 and 16 seater minibuses with luggage space</li>
              <li>Fixed upfront quotes — no surge pricing</li>
              <li>Flight tracking, licensed drivers, meet &amp; greet</li>
              <li>Heathrow, London hotels, events and tours</li>
            </ul>

            <div className="ccg-route-review-strip">
              <CcgPremiumReviews pageKey="/heathrow-minibus-hire" ariaLabel="Heathrow minibus customer reviews" />
            </div>
          </div>
        </section>

        <CcgRouteLhrStatusCompact title="Live Heathrow checks for minibus bookings" />

        <button className="route-floating-book-btn" onClick={scrollToForm}>
          Book Heathrow Minibus
        </button>

        {/* FLEET SECTION */}
        <section className="heathrow-minibus-section" id="fleet">
          <header className="heathrow-minibus-section-header">
            <h2>🚐 Our Heathrow Minibus Fleet</h2>
            <p>
              Comfortable, clean and luggage-friendly vehicles for airport
              transfers, hotel shuttles, events and tours.
            </p>
          </header>

          <div className="heathrow-minibus-fleet-grid">
            <article className="heathrow-minibus-fleet-card">
              <img
                src="/minibus-hero.png"
                alt="9 seater Heathrow minibus"
                className="heathrow-minibus-fleet-img"
              />
              <h3>9-Seater Minibus</h3>
              <ul>
                <li>👥 Up to 8 passengers + driver</li>
                <li>🧳 8–10 medium suitcases</li>
                <li>✅ Perfect for family holidays and friend groups</li>
                <li>✅ Great for hotel runs & cruise transfers</li>
              </ul>
            </article>

            <article className="heathrow-minibus-fleet-card">
              <img
                src="/minibus.jpg"
                alt="14 seater Heathrow minibus"
                className="heathrow-minibus-fleet-img"
              />
              <h3>14-Seater Minibus</h3>
              <ul>
                <li>👥 Up to 14 passengers</li>
                <li>🧳 Plenty of luggage room for group trips</li>
                <li>✅ Ideal for corporate events & school trips</li>
                <li>✅ Popular for weddings & large family travel</li>
              </ul>
            </article>

            <article className="heathrow-minibus-fleet-card subtle-card">
              <h3>Need 20+ Seats?</h3>
              <p>
                For larger groups we can arrange 25–29 seater coaches via our
                trusted PSV partner{" "}
                <strong>RHP Minibus Hire UK Limited</strong>.
              </p>
              <p>
                Tell us your group size and we’ll recommend the best vehicle
                option.
              </p>
              <Link to="/heathrow-coach-hire" className="heathrow-minibus-link">
                View Heathrow coach hire →
              </Link>
            </article>
          </div>

          <p className="heathrow-minibus-fleet-footer">
            For our full fleet including saloon &amp; MPV vehicles, see{" "}
            <Link to="/fleet">CheapCabGrab Fleet</Link>.
          </p>
        </section>

        {/* TYPICAL FARES */}
        <section className="heathrow-minibus-section" id="fares">
          <header className="heathrow-minibus-section-header">
            <h2>💷 Typical Heathrow Minibus Fares &amp; Journey Times</h2>
            <p>
              Guide prices for standard daytime journeys. Final fare is
              confirmed in your quote before you book.
            </p>
          </header>

          <div className="heathrow-minibus-fares-grid">
            <article className="heathrow-minibus-fare-card">
              <h3>Heathrow ↔ Slough / Windsor</h3>
              <ul>
                <li>📍 7–12 miles</li>
                <li>⏱ 20–30 minutes</li>
                <li>💷 9-seater from <strong>£70–£95</strong></li>
                <li>💷 14-seater from <strong>£90–£120</strong></li>
              </ul>
            </article>

            <article className="heathrow-minibus-fare-card">
              <h3>Heathrow ↔ Maidenhead / Reading</h3>
              <ul>
                <li>📍 15–30 miles</li>
                <li>⏱ 30–50 minutes</li>
                <li>💷 9-seater from <strong>£95–£140</strong></li>
                <li>💷 14-seater from <strong>£120–£170</strong></li>
              </ul>
            </article>

            <article className="heathrow-minibus-fare-card">
              <h3>Heathrow ↔ Central London</h3>
              <ul>
                <li>📍 16–22 miles (area-dependent)</li>
                <li>⏱ 45–75 minutes (traffic-dependent)</li>
                <li>💷 9-seater from <strong>£140–£190</strong></li>
                <li>💷 14-seater from <strong>£170–£230</strong></li>
              </ul>
            </article>

            <article className="heathrow-minibus-fare-card">
              <h3>Multi-Stop &amp; Event Trips</h3>
              <p>
                For weddings, corporate events, stadiums or multi-stop
                itineraries, contact our team for a tailored quote.
              </p>
              <Link to="/contact" className="heathrow-minibus-link">
                Contact group travel support →
              </Link>
            </article>
          </div>
          <p className="heathrow-minibus-fares-note">
            Prices depend on exact pickup point, time of day and route.
            Everything is confirmed <strong>upfront</strong> before you pay.
          </p>
        </section>

        {/* FAQ SECTION */}
        <section className="heathrow-minibus-section" id="faq">
          <header className="heathrow-minibus-section-header">
            <h2>❓ Heathrow Minibus FAQs</h2>
            <p>
              A few of the most common questions groups ask before booking
              minibuses at Heathrow.
            </p>
          </header>

          <div className="heathrow-minibus-faq-grid">
            <article className="heathrow-minibus-faq-card">
              <h3>Do you include meet &amp; greet at the terminal?</h3>
              <p>
                Meet &amp; greet is <strong>optional</strong>. Many groups
                prefer kerbside pickup at short-stay car parks to save time and
                charges. We’ll explain both options in your booking confirmation.
              </p>
            </article>

            <article className="heathrow-minibus-faq-card">
              <h3>Can we book child seats?</h3>
              <p>
                Yes. Child and booster seats can be added in advance – please
                tell us the ages of children when booking so we can allocate the
                correct equipment.
              </p>
            </article>

            <article className="heathrow-minibus-faq-card">
              <h3>Do you track flights for delays?</h3>
              <p>
                For most airlines we can track your flight and adjust dispatch
                times where possible. Standard waiting time is included; extra
                waiting or parking is charged at a fair rate which we explain up
                front.
              </p>
            </article>

            <article className="heathrow-minibus-faq-card">
              <h3>How do we book multiple minibuses?</h3>
              <p>
                Large groups can book more than one minibus or a mix of minibuses
                and coaches. Either submit separate quotes or{" "}
                <Link to="/contact">contact our team</Link> with full details.
              </p>
            </article>
          </div>

          <p className="heathrow-minibus-faq-footer">
            For more details on delays, luggage, payments and support, see our{" "}
            <Link to="/faq">full FAQ page</Link>.
          </p>
        </section>

        {/* HOTELS & ATTRACTIONS */}
        <section className="heathrow-minibus-section" id="hotels-attractions">
          <header className="heathrow-minibus-section-header">
            <h2>🏨 Hotels & 🎢 Attractions Served by Our Minibuses</h2>
            <p>
              Perfect for groups staying near Heathrow or visiting nearby
              attractions.
            </p>
          </header>

          <div className="heathrow-minibus-hotels-grid">
            <article className="heathrow-minibus-hotels-card">
              <h3>Heathrow Hotels</h3>
              <p>
                Regular minibus transfers to hotels around Heathrow including
                Sofitel T5, Hilton T4, Premier Inn, Holiday Inn and many more.
              </p>
              <Link to="/heathrow-hotels" className="heathrow-minibus-link">
                View Heathrow hotel guide →
              </Link>
            </article>

            <article className="heathrow-minibus-hotels-card">
              <h3>Nearby Attractions</h3>
              <p>
                Popular group trips include Windsor Castle, Legoland, Thorpe
                Park, Kew Gardens and central London sights.
              </p>
              <Link
                to="/heathrow-attractions"
                className="heathrow-minibus-link"
              >
                View Heathrow attractions guide →
              </Link>
            </article>
          </div>
        </section>

        {/* SUPPORT BANNER */}
        <section className="heathrow-minibus-support">
          <div className="heathrow-minibus-support-inner">
            <div>
            <h2>📞 Need help planning Heathrow group transport?</h2>
              <p>
                Our support team can advise on the right vehicle sizes, loading
                times, child seats and multi-stop routes.
              </p>
            </div>
            <div className="heathrow-minibus-support-actions">
              <Link to="/contact" className="heathrow-minibus-support-btn">
                Contact group travel team →
              </Link>
              <Link to="/" className="heathrow-minibus-support-link">
                Or get an instant quote online →
              </Link>
            </div>
          </div>
        </section>

        <div className="route-bottom-cta">
          <button className="route-bottom-cta-btn" onClick={scrollToForm}>
            Book Heathrow Minibus
          </button>
        </div>

        <div className="heathrow-minibus-bottom-spacer" />
      </main>

      <FloatingSupportChat
        brand="ccg"
        defaultFlow="enquiry"
        position="right"
        showAfterMs={9000}
        nudgeText="Need Heathrow minibus help?"
      />
    </div>
  );
};

export default HeathrowMinibusHire;
